import { useState, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { PICKUP_CONSTANTS, ERROR_MESSAGES, SUCCESS_MESSAGES } from './constants';
import { toTwoDecimals } from '../../plugins';

/**
 * Custom hook to manage pickup form state and validation
 * Separates form logic from UI concerns
 */
const usePickupForm = (order, navigate) => {
    const [date, setDate] = useState(null);
    const [totalPrice, setTotalPrice] = useState('');
    const [note, setNote] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Auto-fill payment amount when order loads
    const initializePaymentAmount = useCallback((orderData) => {
        if (orderData?.DueAmount?.value) {
            setTotalPrice(orderData.DueAmount.value.toString());
        }
    }, []);

    // Memoized validation logic
    const validateForm = useCallback((customerId) => {
        const errors = [];

        if (!date) {
            errors.push(ERROR_MESSAGES.MISSING_DATE);
        }

        if (!totalPrice || totalPrice.trim() === '') {
            errors.push(ERROR_MESSAGES.MISSING_AMOUNT);
        }

        const amount = parseFloat(totalPrice);
        if (totalPrice && isNaN(amount)) {
            errors.push(ERROR_MESSAGES.INVALID_AMOUNT);
        }

        if (totalPrice && amount <= PICKUP_CONSTANTS.MIN_PAYMENT_AMOUNT) {
            errors.push(ERROR_MESSAGES.AMOUNT_TOO_LOW);
        }

        // Validate that pickup amount doesn't exceed due amount
        if (totalPrice && order?.DueAmount?.value) {
            const dueAmount = parseFloat(order.DueAmount.value);
            if (!isNaN(amount) && !isNaN(dueAmount) && amount > dueAmount) {
                errors.push(
                    `${ERROR_MESSAGES.AMOUNT_EXCEEDS_DUE} $${toTwoDecimals(dueAmount)}.`
                );
            }
        }

        if (!customerId) {
            errors.push(ERROR_MESSAGES.MISSING_CUSTOMER);
        }

        return errors;
    }, [date, totalPrice, order]);

    // Handle price input with validation
    const handlePriceChange = useCallback((event) => {
        const value = event.target.value;

        // Allow only numbers and decimal point
        if (value === '' || /^\d*\.?\d*$/.test(value)) {
            // Optional: Real-time validation to prevent exceeding due amount
            if (value && order?.DueAmount?.value) {
                const enteredAmount = parseFloat(value);
                const dueAmount = parseFloat(order.DueAmount.value);

                // Only update if amount doesn't exceed due amount
                if (!isNaN(enteredAmount) && !isNaN(dueAmount) && enteredAmount > dueAmount) {
                    // Optionally cap at due amount instead of blocking
                    // setTotalPrice(dueAmount.toString());
                    // Or just prevent the input
                    return;
                }
            }
            setTotalPrice(value);
        }
    }, [order]);

    // Reset form to initial state
    const resetForm = useCallback(() => {
        setDate(null);
        setTotalPrice('');
        setNote('');
    }, []);

    // Handle form submission
    const handleSubmit = useCallback(async (e, customerId, addPickUpPayment) => {
        e.preventDefault();

        const validationErrors = validateForm(customerId);
        if (validationErrors.length > 0) {
            validationErrors.forEach(error => toast.error(error));
            return;
        }

        setIsSubmitting(true);

        try {
            const formData = {
                note: { value: note },
                CustomerID: { value: customerId },
                PickUpAmount: { value: parseFloat(totalPrice) },
                PickUpDate: { value: date },
                OrderNumber: { value: order.OrderNbr.value }
            };

            await addPickUpPayment(formData);
            toast.success(SUCCESS_MESSAGES.SUBMISSION_SUCCESS);
            resetForm();
            navigate('/profile/open-invoices');
        } catch (error) {
            toast.error(ERROR_MESSAGES.SUBMISSION_FAILED);
            console.error('Pickup submission error:', error);
        } finally {
            setIsSubmitting(false);
        }
    }, [date, totalPrice, note, order, validateForm, resetForm, navigate]);

    // Calculate remaining characters for note
    const remainingChars = useMemo(() =>
        PICKUP_CONSTANTS.MAX_NOTE_LENGTH - note.length,
        [note]
    );

    return {
        // State
        date,
        totalPrice,
        note,
        isSubmitting,
        remainingChars,

        // Actions
        setDate,
        setNote,
        handlePriceChange,
        handleSubmit,
        initializePaymentAmount,

        // Constants
        MAX_NOTE_LENGTH: PICKUP_CONSTANTS.MAX_NOTE_LENGTH,
    };
};

export default usePickupForm;

